/**
 * Utility to ensure all images are loaded before performing actions like printing
 * Handles both regular img elements and picture elements with source tags
 */

interface WaitForImagesOptions {
  onProgress?: (loaded: number, total: number) => void; // callback for progress tracking
}

/**
 * Waits for all images on the page to load
 * @param options Configuration options
 * @returns Promise that resolves when all images are loaded or errored
 */
export function waitForAllImages(options: WaitForImagesOptions = {}): Promise<void> {
  const { onProgress } = options;

  // Get all img elements
  const imgElements = Array.from(document.querySelectorAll<HTMLImageElement>('img'));

  // Get all picture elements and their img children
  const pictureElements = Array.from(document.querySelectorAll('picture'));
  const pictureImgs = pictureElements.flatMap((picture) =>
    Array.from(picture.querySelectorAll<HTMLImageElement>('img'))
  );

  // Combine and deduplicate
  const allImages = Array.from(new Set([...imgElements, ...pictureImgs]));
  if (allImages.length === 0) {
    // No images to wait for
    return Promise.resolve();
  }

  let loadedCount = 0;

  const imageLoadPromises = allImages.map(
    (img) =>
      new Promise<void>((resolve) => {
        // If image is already loaded, resolve immediately
        if (img.complete && img.naturalHeight > 0) {
          loadedCount += 1;
          onProgress?.(loadedCount, allImages.length);
          resolve();
          return;
        }

        // If image failed to load (naturalHeight === 0 but complete === true), resolve anyway
        if (img.complete && img.naturalHeight === 0) {
          loadedCount += 1;
          onProgress?.(loadedCount, allImages.length);
          resolve();
          return;
        }

        // Set up event listeners for image load/error
        const onLoad = () => {
          loadedCount += 1;
          onProgress?.(loadedCount, allImages.length);
          cleanup();
          resolve();
        };

        const onError = () => {
          // Treat errors as resolved - we want to print even if some images failed
          loadedCount += 1;
          onProgress?.(loadedCount, allImages.length);
          cleanup();
          resolve();
        };

        const cleanup = () => {
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onError);
        };

        img.addEventListener('load', onLoad);
        img.addEventListener('error', onError);
      })
  );

  return Promise.all(imageLoadPromises).then(() => undefined);
}

/**
 * Force load lazy-loaded images by triggering intersection observer
 * This is useful for images with loading="lazy" attribute
 */
export function forceLazyLoadImages(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const lazyImages = Array.from(document.querySelectorAll<HTMLImageElement>('img[loading="lazy"]'));

  lazyImages.forEach((img) => {
    // Temporarily set loading to eager to force load
    const originalLoading = img.loading;
    img.loading = 'eager';

    // Restore original loading attribute after a short delay
    setTimeout(() => {
      img.loading = originalLoading;
    }, 0);
  });
}
